import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ManagingCompanyGlobalAuthorityRequestsDatasource } from '@app/managing-company-administration/managing-company-authority-requests/managing-company-global-authority-requests.datasource';
import { GlobalAuthorityRequestService } from '@services/user-global-authority-request/global-authroity-request.service';
import { MatButton } from '@angular/material/button';
import { ToastService } from '@services/toast/toast.service';
import { GlobalAuthorityRequest, Status } from '@app/shared/models/user-global-authority-request-interface';

@Component({
  selector: 'app-managing-company-authority-requests',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatPaginatorModule, MatProgressSpinnerModule, MatButton],
  templateUrl: './managing-company-authority-requests.component.html',
  styleUrl: './managing-company-authority-requests.component.scss'
})
export class ManagingCompanyAuthorityRequestsComponent implements OnInit, OnDestroy {
  dataSource: ManagingCompanyGlobalAuthorityRequestsDatasource;
  displayedColumns: string[] = [
    'id',
    'avatar',
    'name',
    'emailAddress',
    'requestGlobalAuthority',
    'projectName',
    'managingCompanyName',
    'requestDate',
    'authSource',
    'actions'
  ];
  managingCompanyId: number | null = null;
  pageIndex = 0;
  pageSize = 10;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private globalAuthorityRequestService: GlobalAuthorityRequestService,
    private toast: ToastService
  ) {
    this.dataSource = new ManagingCompanyGlobalAuthorityRequestsDatasource(this.globalAuthorityRequestService);
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(
        map((params) => Number(params.get('managingCompanyId'))),
        takeUntil(this.destroy$)
      )
      .subscribe((managingCompanyId) => {
        if (!Number.isFinite(managingCompanyId)) {
          return;
        }

        this.managingCompanyId = managingCompanyId;
        this.pageIndex = 0;
        this.loadRequestsPage();
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRequestsPage();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSource.disconnect();
  }

  acceptRequest(globalAuthorityRequest: GlobalAuthorityRequest): void {
    if (this.managingCompanyId == null) {
      return;
    }

    this.globalAuthorityRequestService.changeStatusWithManagingCompanyAuthority(globalAuthorityRequest.id, this.managingCompanyId, globalAuthorityRequest.status, Status.APPROVED)
      .subscribe({
        next: () => {
          this.loadRequestsPage();
          this.toast.success({
            message: `Successfully granted ${globalAuthorityRequest.globalAuthority.toString()} to ${
              globalAuthorityRequest.user.name
            }`,
            dataCy: 'managing-company-grant-global-authority-success-toast'
          });
        },
        error: (error) => {
          const message = error.message.includes('400')
            ? 'Request was not in the expected state, please refresh the page'
            : error.message;

          this.toast.warning({
            message: `Failed to grant ${globalAuthorityRequest.globalAuthority.toString()} to ${
              globalAuthorityRequest.user.name
            } with error: ${message}`,
            dataCy: 'managing-company-grant-global-authority-failed-toast'
          });
        }
      });
  }

  declineRequest(globalAuthorityRequest: GlobalAuthorityRequest): void {
    if (this.managingCompanyId == null) {
      return;
    }

    this.globalAuthorityRequestService
      .changeStatusWithManagingCompanyAuthority(
        globalAuthorityRequest.id,
        this.managingCompanyId,
        globalAuthorityRequest.status,
        Status.DENIED
      )
      .subscribe({
        next: () => {
          this.loadRequestsPage();
          this.toast.success({
            message: `Successfully declined ${globalAuthorityRequest.globalAuthority.toString()} to ${
              globalAuthorityRequest.user.name
            }`,
            dataCy: 'managing-company-decline-global-authority-success-toast'
          });
        },
        error: (error) => {
          const message = error.message.includes('400')
            ? 'Request was not in the expected state, please refresh the page'
            : error.message;

          this.toast.warning({
            message: `Failed to decline ${globalAuthorityRequest.globalAuthority.toString()} to ${
              globalAuthorityRequest.user.name
            } with error: ${message}`,
            dataCy: 'managing-company-decline-global-authority-failed-toast'
          });
        }
      });
  }

  loadRequestsPage(): void {
    if (this.managingCompanyId == null) {
      return;
    }

    this.dataSource.loadGlobalAuthorityRequests(this.managingCompanyId, this.pageIndex, this.pageSize);
  }
}
